import { BaseEntity } from './base.entity';
import type { ToolExecutionDTO, SourceReferenceDTO } from '../dtos/tool-execution.dto';

export type ChatRole = 'user' | 'assistant';

export class Chat extends BaseEntity {
  id: string;
  room_id: string;
  user_id: string | null; // null for assistant messages
  role: ChatRole;
  message: string;
  metadata: Record<string, any> | null; // For storing additional data like model info, tokens, etc.
  is_loading: boolean;
  source_references: SourceReferenceDTO[] | null; // References to documents used in generating the response
  tool_executions: ToolExecutionDTO[] | null; // Stores function calling tool executions for assistant messages
  tool_messages: string[] | null; // Messages displayed while tools are being executed
  error_message: string | null; // Error message if something went wrong
  change_proposal_id: string | null; // ID of the change proposal if one was created
  created_at: Date;
  updated_at: Date;

  constructor(data: {
    id: string;
    room_id: string;
    user_id: string | null;
    role: ChatRole;
    message: string;
    metadata: Record<string, any> | null;
    is_loading: boolean;
    source_references: SourceReferenceDTO[] | null;
    tool_executions: ToolExecutionDTO[] | null;
    tool_messages: string[] | null;
    error_message: string | null;
    change_proposal_id: string | null;
    created_at: Date;
    updated_at: Date;
  }) {
    super();
    this.id = data.id;
    this.room_id = data.room_id;
    this.user_id = data.user_id;
    this.role = data.role;
    this.message = data.message;
    this.metadata = data.metadata;
    this.is_loading = data.is_loading;
    this.source_references = data.source_references;
    this.tool_executions = data.tool_executions;
    this.tool_messages = data.tool_messages;
    this.error_message = data.error_message;
    this.change_proposal_id = data.change_proposal_id;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.setOriginalData(this.toData());
  }

  /**
   * Update message content
   */
  updateMessage(message: string): void {
    this.message = message;
    this.updated_at = new Date();
    this.markAsDirty();
  }

  /**
   * Update metadata
   */
  updateMetadata(metadata: Record<string, any>): void {
    this.metadata = { ...this.metadata, ...metadata };
    this.updated_at = new Date();
    this.markAsDirty();
  }

  /**
   * Check if message is from user
   */
  isUserMessage(): boolean {
    return this.role === 'user';
  }

  /**
   * Check if message is from assistant
   */
  isAssistantMessage(): boolean {
    return this.role === 'assistant';
  }

  /**
   * Convert to plain data object
   */
  toData(): any {
    return {
      id: this.id,
      room_id: this.room_id,
      user_id: this.user_id,
      role: this.role,
      message: this.message,
      metadata: this.metadata,
      is_loading: this.is_loading,
      source_references: this.source_references,
      tool_executions: this.tool_executions,
      tool_messages: this.tool_messages,
      error_message: this.error_message,
      change_proposal_id: this.change_proposal_id,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  /**
   * Create Chat instance from database row
   */
  static fromData(data: any): Chat {
    return new Chat({
      id: data.id,
      room_id: data.room_id,
      user_id: data.user_id,
      role: data.role,
      message: data.message,
      metadata: data.metadata,
      is_loading: data.is_loading || false,
      source_references: data.source_references || null,
      tool_executions: data.tool_executions || null,
      tool_messages: data.tool_messages || null,
      error_message: data.error_message || null,
      change_proposal_id: data.change_proposal_id || null,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at)
    });
  }
}

export interface ChatInsert {
  room_id: string;
  user_id?: string | null;
  role: ChatRole;
  message: string;
  metadata?: Record<string, any> | null;
  is_loading?: boolean;
  source_references?: SourceReferenceDTO[] | null;
  tool_executions?: ToolExecutionDTO[] | null;
  tool_messages?: string[] | null;
  error_message?: string | null;
  change_proposal_id?: string | null;
}

export interface ChatUpdate {
  message?: string;
  metadata?: Record<string, any> | null;
  is_loading?: boolean;
  source_references?: SourceReferenceDTO[] | null;
  tool_executions?: ToolExecutionDTO[] | null;
  tool_messages?: string[] | null;
  error_message?: string | null;
  change_proposal_id?: string | null;
}